// Permission Types
export type ResourceType = 
  | 'campaigns' 
  | 'analytics' 
  | 'users' 
  | 'properties' 
  | 'locations'      // New: Location management
  | 'qr_codes'       // New: QR code management
  | 'prizes' 
  | 'sessions' 
  | 'reports' 
  | 'settings'
  | 'notifications';

export type ActionType = 
  | 'create' 
  | 'read' 
  | 'update' 
  | 'delete' 
  | 'manage';

export type UserRole = 
  | 'super_admin' 
  | 'client_admin' 
  | 'property_manager' 
  | 'regional_manager'   // New: Manages multiple properties in a region
  | 'location_manager'   // New: Manages specific locations within a property
  | 'campaign_manager' 
  | 'analyst' 
  | 'front_desk' 
  | 'custom';

export interface Permission {
  resource: ResourceType;
  actions: ActionType[];
  scope?: {
    clientIds?: string[];
    propertyIds?: string[];
    locationIds?: string[];     // New: Location-level permissions
    campaignIds?: string[];
  };
}

export interface UserClaims {
  role: UserRole;
  permissions: Permission[];
  clientId?: string;            // Single client assignment
  propertyIds?: string[];
  locationIds?: string[];       // New: Location access
  campaignIds?: string[];
  metadata?: {
    createdAt: string;
    updatedAt: string;
    createdBy: string;
  };
}

export interface PermissionChecker {
  hasPermission(resource: ResourceType, action: ActionType, scope?: { 
    clientId?: string, 
    propertyId?: string, 
    locationId?: string,        // New: Location scope checking
    campaignId?: string 
  }): boolean;
  canAccessClient(clientId: string): boolean;
  canAccessProperty(propertyId: string): boolean;
  canAccessLocation(locationId: string): boolean;  // New: Location access checking
  canAccessCampaign(campaignId: string): boolean;
  userRole(): UserRole | null;
}

// Default role templates
export const DEFAULT_ROLE_TEMPLATES = [
  {
    role: 'super_admin' as UserRole,
    permissions: [
      { resource: 'campaigns' as ResourceType, actions: ['create', 'read', 'update', 'delete', 'manage'] as ActionType[] },
      { resource: 'analytics' as ResourceType, actions: ['read', 'manage'] as ActionType[] },
      { resource: 'users' as ResourceType, actions: ['create', 'read', 'update', 'delete', 'manage'] as ActionType[] },
      { resource: 'properties' as ResourceType, actions: ['create', 'read', 'update', 'delete', 'manage'] as ActionType[] },
      { resource: 'locations' as ResourceType, actions: ['create', 'read', 'update', 'delete', 'manage'] as ActionType[] },
      { resource: 'qr_codes' as ResourceType, actions: ['create', 'read', 'update', 'delete', 'manage'] as ActionType[] },
      { resource: 'prizes' as ResourceType, actions: ['create', 'read', 'update', 'delete', 'manage'] as ActionType[] },
      { resource: 'sessions' as ResourceType, actions: ['read', 'update', 'manage'] as ActionType[] },
      { resource: 'reports' as ResourceType, actions: ['read', 'manage'] as ActionType[] },
      { resource: 'settings' as ResourceType, actions: ['read', 'update', 'manage'] as ActionType[] },
      { resource: 'notifications' as ResourceType, actions: ['read', 'manage'] as ActionType[] }
    ]
  },
  {
    role: 'client_admin' as UserRole,
    permissions: [
      { resource: 'campaigns' as ResourceType, actions: ['create', 'read', 'update', 'delete', 'manage'] as ActionType[] },
      { resource: 'analytics' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'users' as ResourceType, actions: ['create', 'read', 'update', 'manage'] as ActionType[] },
      { resource: 'properties' as ResourceType, actions: ['create', 'read', 'update'] as ActionType[] },
      { resource: 'locations' as ResourceType, actions: ['create', 'read', 'update', 'delete'] as ActionType[] },
      { resource: 'qr_codes' as ResourceType, actions: ['create', 'read', 'update', 'delete'] as ActionType[] },
      { resource: 'prizes' as ResourceType, actions: ['create', 'read', 'update', 'delete'] as ActionType[] },
      { resource: 'sessions' as ResourceType, actions: ['read', 'manage'] as ActionType[] },
      { resource: 'reports' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'settings' as ResourceType, actions: ['read', 'update'] as ActionType[] },
      { resource: 'notifications' as ResourceType, actions: ['read'] as ActionType[] }
    ]
  },
  {
    role: 'regional_manager' as UserRole,
    permissions: [
      { resource: 'campaigns' as ResourceType, actions: ['create', 'read', 'update'] as ActionType[] },
      { resource: 'analytics' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'properties' as ResourceType, actions: ['read', 'update'] as ActionType[] },
      { resource: 'locations' as ResourceType, actions: ['create', 'read', 'update', 'delete'] as ActionType[] },
      { resource: 'qr_codes' as ResourceType, actions: ['create', 'read', 'update', 'delete'] as ActionType[] },
      { resource: 'prizes' as ResourceType, actions: ['read', 'update'] as ActionType[] },
      { resource: 'sessions' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'settings' as ResourceType, actions: ['read'] as ActionType[] }
    ]
  },
  {
    role: 'property_manager' as UserRole,
    permissions: [
      { resource: 'campaigns' as ResourceType, actions: ['read', 'update'] as ActionType[] },
      { resource: 'analytics' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'locations' as ResourceType, actions: ['create', 'read', 'update', 'delete'] as ActionType[] },
      { resource: 'qr_codes' as ResourceType, actions: ['create', 'read', 'update', 'delete'] as ActionType[] },
      { resource: 'prizes' as ResourceType, actions: ['read', 'update'] as ActionType[] },
      { resource: 'sessions' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'settings' as ResourceType, actions: ['read'] as ActionType[] }
    ]
  },
  {
    role: 'location_manager' as UserRole,
    permissions: [
      { resource: 'campaigns' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'analytics' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'locations' as ResourceType, actions: ['read', 'update'] as ActionType[] },
      { resource: 'qr_codes' as ResourceType, actions: ['create', 'read', 'update'] as ActionType[] },
      { resource: 'prizes' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'sessions' as ResourceType, actions: ['read'] as ActionType[] }
    ]
  },
  {
    role: 'campaign_manager' as UserRole,
    permissions: [
      { resource: 'campaigns' as ResourceType, actions: ['create', 'read', 'update'] as ActionType[] },
      { resource: 'analytics' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'qr_codes' as ResourceType, actions: ['create', 'read', 'update'] as ActionType[] },
      { resource: 'prizes' as ResourceType, actions: ['create', 'read', 'update'] as ActionType[] },
      { resource: 'sessions' as ResourceType, actions: ['read'] as ActionType[] }
    ]
  },
  {
    role: 'analyst' as UserRole,
    permissions: [
      { resource: 'campaigns' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'analytics' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'sessions' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'reports' as ResourceType, actions: ['read'] as ActionType[] }
    ]
  },
  {
    role: 'front_desk' as UserRole,
    permissions: [
      { resource: 'sessions' as ResourceType, actions: ['read'] as ActionType[] },
      { resource: 'prizes' as ResourceType, actions: ['read'] as ActionType[] }
    ]
  }
];
